import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Activity, ArrowLeft, Mail, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setEmailSent(true);
      toast.success('Password reset email sent!');
    } catch (error) {
      // Don't reveal if email exists or not for security
      setEmailSent(true);
      toast.success('If an account exists with this email, you will receive a reset link.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 p-12 flex-col justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
            <Activity className="w-6 h-6 text-white" strokeWidth={2} />
          </div>
          <div>
            <h1 className="text-white font-semibold text-xl tracking-tight font-[Manrope]">Pulse</h1>
            <p className="text-slate-500 text-sm">Players Health</p>
          </div>
        </div>
        
        <div>
          <h2 className="text-4xl font-bold text-white font-[Manrope] tracking-tight mb-4">
            Reset Your Password
          </h2>
          <p className="text-slate-400 text-lg leading-relaxed">
            Enter your email address and we'll send you a link to reset your password.
          </p>
        </div>

        <div className="text-slate-500 text-sm">
          © {new Date().getFullYear()} Players Health. All rights reserved.
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center">
              <Activity className="w-6 h-6 text-white" strokeWidth={2} />
            </div>
            <div>
              <h1 className="text-slate-900 font-semibold text-xl tracking-tight font-[Manrope]">Pulse</h1>
              <p className="text-slate-500 text-sm">Players Health</p>
            </div>
          </div>

          {!emailSent ? (
            <>
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-slate-900 font-[Manrope]">Forgot Password?</h2>
                <p className="text-slate-500 mt-1">No worries, we'll send you reset instructions.</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@playershealth.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="forgot-email-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={isLoading}
                  data-testid="forgot-submit-btn"
                >
                  {isLoading ? 'Sending...' : 'Send Reset Link'}
                  <Mail className="w-4 h-4 ml-2" />
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 font-[Manrope] mb-2">Check Your Email</h2>
              <p className="text-slate-500 mb-6">
                We sent a password reset link to<br/>
                <span className="font-medium text-slate-700">{email}</span>
              </p>
              <p className="text-sm text-slate-400 mb-6">
                Didn't receive the email? Check your spam folder or{' '}
                <button 
                  onClick={() => setEmailSent(false)} 
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  try again
                </button>
              </p>
            </div>
          )}

          <div className="mt-6 text-center">
            <Link 
              to="/login" 
              className="inline-flex items-center text-sm text-slate-500 hover:text-slate-700"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
